import { useEffect, useState } from "react";

type PresetInstructions = {
  [key: string]: string[];
};

const PRESET_INSTRUCTIONS: PresetInstructions = {
  ZF: [
    "Fixez bien la croix centrale sans bouger les yeux. Appuyez sur la barre espace le plus rapidement possible dès qu’une cible lumineuse apparait. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  ZPL: [
    "Fixez bien la croix au centre de l’écran sans bouger les yeux. Votre objectif est de pointer du doigt toutes les cibles que vous pourrez percevoir tout en maintenant la fixation au centre. Une fois une cible pointée, laissez votre doigt sur l’écran et vérifiez en regardant. Prenez autant de temps que nécessaire et n’hésitez pas à pointer à nouveau les cibles manquées en fixant bien à nouveau la croix centrale. Appuyez sur la barre espace pour passer d’un essai à l’autre. Appuyez sur la barre espace pour lire la suite des instructions.",
    "Si vous souhaitez faire une pause ou interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  ZPI: [
    "Fixez bien la croix au centre de l’écran sans bouger les yeux. Votre objectif est de décrire et identifier toutes les cibles que vous pourrez percevoir tout en maintenant la fixation au centre. Prenez autant de temps que nécessaire pour identifier la taille, la forme, l’orientation, le mouvement, la couleur, etc. Une fois ce travail effectué pour toutes les cibles présentées à l’écran, contrôlez visuellement puis revenez au centre de l’écran et recommencez en essayant d’affiner votre perception. Appuyez sur la barre espace pour passer d’un essai à l’autre. Appuyez sur la barre espace pour lire la suite des instructions.",
    "Si vous souhaitez faire une pause ou interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  ZPCCF: [
    "Fixez bien la croix au centre de l’écran sans bouger les yeux. Des ronds de couleur vont apparaitre à gauche et à droite de la croix. Vous devez essayer de déterminer si le rond de droite et celui de gauche sont de la même couleur. Appuyez sur la barre espace pour lire la suite des instructions.",
    "Veillez à garder votre regard sur la croix centrale durant toute la durée de l’essai. Au bout de 10 secondes, une question apparaitra : « Les couleurs des cibles présentées sont-elles les mêmes à gauche et à droite ? » Vous devrez alors répondre par oui ou par non. Appuyez sur la barre espace pour lire la suite des instructions.",
    "Si vous souhaitez faire une pause ou interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  ZPFCF: [
    "Fixez bien la croix au centre de l’écran sans bouger les yeux. Une forme va apparaitre à gauche et à droite de la croix. Vous devez essayer de déterminer si la forme de droite et celle de gauche sont les mêmes. Appuyez sur la barre espace pour lire la suite des instructions.",
    "Veillez à garder votre regard sur la croix centrale durant toute la durée de l’essai. Au bout de 10 secondes, une question apparaitra : « La forme des cibles présentées est-elle les mêmes à gauche et à droite ? » Vous devrez alors répondre par oui ou par non. Appuyez sur la barre espace pour lire la suite des instructions.",
    "Si vous souhaitez faire une pause ou interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  PVS: [
    "Cet exercice vise à réentraîner la mobilité de vos yeux. Une cible va apparaître au centre de l’écran, disparaître, puis réapparaître à différents endroits de l’écran. Votre tâche consiste à la retrouver le plus rapidement possible. Essayez de ne jamais la quitter des yeux. Des pauses de 10 secondes vous seront proposées toutes les minutes. Si vous souhaitez interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  OT: [
    "Cet exercice vise à réentraîner vos capacités de lecture. Un texte va défiler au centre de l’écran de droite à gauche. Essayez de le lire à voix haute. Une croix centrale apparaît entre chaque essai. Appuyez sur la barre espace pour passer à la phrase suivante. Si vous souhaitez interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  OB: [
    "Cet exercice vise à réentraîner vos capacités de lecture. Une suite de barres va défiler au centre de l’écran de droite à gauche. Essayez de les regarder défiler comme si vous regardiez un train passer. Une croix centrale apparaît entre chaque essai. Appuyez sur la barre espace pour passer à l’essai suivant. Si vous souhaitez interrompre l’exercice en cours, appuyez sur le bouton ECHAP de votre clavier. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  "bilan-champ-visuel": [
    "Fixez bien la croix au centre de l’écran sans bouger les yeux. Des cercles lumineux vont apparaitre de part et d’autre de cette croix. Appuyez sur la barre espace le plus rapidement possible dès qu’une cible lumineuse apparait. Veillez à garder votre regard sur la croix centrale durant toute la durée du bilan. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
  "bilan-lecture": [
    "Un texte va apparaitre au centre de l’écran. Lisez ce texte à voix haute. Une fois votre lecture terminée, appuyez sur espace. Appuyez sur la barre espace pour lancer l’exercice.",
  ],
};

type EcranInstructionsProps = {
  instructions?: string[];
  preset?: keyof PresetInstructions; // Clé pour sélectionner un preset
  options?: {
    dynamicDisplay?: boolean; // Afficher les consignes de manière dynamique
    fontSizeMultiplier?: number; // Multiplier la taille de la police
    textColor?: string; // Couleur personnalisée du texte
  };
  setShowInstructions: (show: boolean) => void;
  startSession?: () => void; // Fonction pour démarrer la session
  currentModuleIndex?: number; // Index du module actuel pour démarrer la session
};

const EcranInstructionsPreset = ({
  instructions,
  preset,
  options = {},
  setShowInstructions,
  startSession,
  currentModuleIndex,
}: EcranInstructionsProps) => {
  const {
    dynamicDisplay = false,
    fontSizeMultiplier = 2,
    textColor = "white",
  } = options;
  const presetInstructions = preset ? PRESET_INSTRUCTIONS[preset] : [];
  const displayedInstructions = instructions || presetInstructions;

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleStartSession = () => {
    if (currentModuleIndex === 0 && startSession) {
      startSession();
    }
    setShowInstructions(false);
  }

  const handleKeyPress = (event: KeyboardEvent) => {
    if (dynamicDisplay && event.code === "Space") {
      if (currentIndex < displayedInstructions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        handleStartSession();
      }
    }

    if (event.code === "Space" && !dynamicDisplay) {
      handleStartSession();
    }
  };

  // Ajouter l'écouteur de clavier pour la navigation
  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [currentIndex, dynamicDisplay]);

  return (
    <div
      style={{
        backgroundColor: "black",
        color: textColor,
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          backgroundColor: "black",
          color: textColor,
          height: "100vh",
          display: "flex",
          justifyContent: "left",
          alignItems: "left",
          textAlign: "left",
          padding: "20px",
        }}
      >
        <ul
          style={{
            fontSize: `${fontSizeMultiplier}rem`,
            lineHeight: "1.5",
            maxWidth: "800px",
            listStyleType: "none", // pas de puces par défaut
            paddingLeft: "20px",
            margin: 0,
          }}
          className="flex flex-col justify-center"
        >
          {displayedInstructions[currentIndex]
            .split(".")
            .map((segment) => segment.trim())
            .filter((segment) => segment !== "")
            .map((segment, index) => (
              <li
                key={index}
                style={{
                  marginBottom: "10px"}}
                  className="flex flex-start justify-start items-center"
              >
                <span>{segment}.</span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

export default EcranInstructionsPreset;
